import React from 'react'
import { useRoadmapStore } from '../../stores/projetos/roadmapStore'
import ItemCard from './ItemCard'

const monthLabels = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']

type MonthCell = { year: number; month: number }

function parseISO(iso?: string | null): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d
}

function addMonths(date: Date, diff: number): Date {
  const d = new Date(date)
  d.setMonth(d.getMonth() + diff)
  return d
}

function monthsRange(start: Date, end: Date): MonthCell[] {
  const cells: MonthCell[] = []
  const cur = new Date(start.getFullYear(), start.getMonth(), 1)
  const last = new Date(end.getFullYear(), end.getMonth(), 1)
  while (cur <= last) {
    cells.push({ year: cur.getFullYear(), month: cur.getMonth() + 1 })
    cur.setMonth(cur.getMonth() + 1)
  }
  return cells
}

function monthIndexFromBase(date: Date, base: MonthCell): number {
  const diff = (date.getFullYear() - base.year) * 12 + (date.getMonth() + 1 - base.month)
  return diff + 1 // 1-based
}

export const RoadmapTimeline: React.FC = () => {
  const { groups, items, labels } = useRoadmapStore()
  const labelsMap = React.useMemo(() => Object.fromEntries(labels.map(l => [l.id, l])), [labels])
  const [expanded, setExpanded] = React.useState(false)
  
  // Mapa de itens por ID para acesso rápido
  const itemsMap = React.useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items])

  // Calcular faixa de meses com base nos itens (menor início e maior fim)
  const { minMax, months, gridColsStyle } = React.useMemo(() => {
    const datesStart: Date[] = []
    const datesEnd: Date[] = []
    items.forEach((it) => {
      const s = parseISO(it.start_date || null)
      const e = parseISO(it.end_date || null) || (s ? addMonths(s, 1) : null)
      if (s) datesStart.push(s)
      if (e) datesEnd.push(e)
    })
    const minStart = datesStart.length ? new Date(Math.min(...datesStart.map(d => d.getTime()))) : new Date(new Date().getFullYear(), 0, 1)
    const maxEnd = datesEnd.length ? new Date(Math.max(...datesEnd.map(d => d.getTime()))) : new Date(new Date().getFullYear(), 11, 1)
    const range = monthsRange(minStart, maxEnd)
    const style = { gridTemplateColumns: `repeat(${range.length}, 120px)` }
    return { minMax: { start: { year: range[0].year, month: range[0].month }}, months: range, gridColsStyle: style }
  }, [items])

  const fullGridColsStyle = React.useMemo(() => ({
    gridTemplateColumns: `160px repeat(${months.length}, 120px)`
  }), [months.length])

  // Altura por grupo baseada no número de "lanes" necessárias (para alinhar o título do grupo)
  const groupLaneCount: Record<string, number> = React.useMemo(() => {
    const counts: Record<string, number> = {}
    groups.forEach((g) => {
      const groupItems = items.filter(i => i.group_id === g.id)
      type PosItem = { colStart: number; colEnd: number }
      const pos: PosItem[] = groupItems.map((it) => {
        const s = parseISO(it.start_date || null)
        const e = parseISO(it.end_date || null) || (s ? addMonths(s, 1) : null)
        if (!s || !e) return null as any
        const cs = Math.max(1, monthIndexFromBase(new Date(s.getFullYear(), s.getMonth(), 1), minMax.start))
        const ce = Math.max(cs + 1, monthIndexFromBase(new Date(e.getFullYear(), e.getMonth(), 1), minMax.start) + 1)
        return { colStart: cs, colEnd: ce }
      }).filter(Boolean)
      const sorted = [...pos].sort((a, b) => a.colStart - b.colStart)
      const laneEnds: number[] = []
      sorted.forEach((pi) => {
        let placed = false
        for (let lane = 0; lane < laneEnds.length; lane++) {
          if (pi.colStart > laneEnds[lane]) {
            laneEnds[lane] = pi.colEnd
            placed = true
            break
          }
        }
        if (!placed) laneEnds.push(pi.colEnd)
      })
      counts[g.id] = Math.max(1, laneEnds.length)
    })
    return counts
  }, [groups, items, minMax])

  const renderBody = () => (
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* Header: 1ª coluna (nomes dos grupos) sem borda extra + meses */}
            <div className="grid gap-0 text-[11px] text-gray-600 dark:text-gray-300 h-8 items-center" style={fullGridColsStyle}>
              <div></div>
              {months.map((m, idx) => (
                <div key={`${m.year}-${m.month}-${idx}`} className="text-center border-b border-gray-200 dark:border-gray-700">
                  {monthLabels[m.month - 1]} {m.year}
                </div>
              ))}
            </div>

            {/* Linhas por grupo (nome + timeline na MESMA grid) */}
            {groups.map((g, idx) => {
                const groupItems = items.filter(i => i.group_id === g.id)

                // Converter itens em posições de colunas (início/fim)
                type PosItem = { id: string; title: string; colStart: number; colEnd: number }
                const posItems: PosItem[] = groupItems.map((it) => {
                  const startDate = parseISO(it.start_date || null)
                  const endDate = parseISO(it.end_date || null) || (startDate ? addMonths(startDate, 1) : null)
                  if (!startDate || !endDate) return null as any
                  const colStart = Math.max(1,
                    monthIndexFromBase(new Date(startDate.getFullYear(), startDate.getMonth(), 1), minMax.start)
                  )
                  const colEnd = Math.max(colStart + 1,
                    monthIndexFromBase(new Date(endDate.getFullYear(), endDate.getMonth(), 1), minMax.start) + 1
                  )
                  return { id: it.id, title: it.title, colStart, colEnd }
                }).filter(Boolean)

                // Alocar "lanes" (faixas) para evitar sobreposição vertical dentro do grupo
                // Estratégia gulosa: ordenar por início e empilhar onde houver espaço
                const sorted = [...posItems].sort((a, b) => a.colStart - b.colStart)
                const laneEnds: number[] = [] // coluna final de cada lane
                const laneOf: Record<string, number> = {}
                sorted.forEach((pi) => {
                  let placed = false
                  for (let lane = 0; lane < laneEnds.length; lane++) {
                    if (pi.colStart > laneEnds[lane]) {
                      laneEnds[lane] = pi.colEnd
                      laneOf[pi.id] = lane
                      placed = true
                      break
                    }
                  }
                  if (!placed) {
                    laneEnds.push(pi.colEnd)
                    laneOf[pi.id] = laneEnds.length - 1
                  }
                })
                const lanesCount = Math.max(1, laneEnds.length)
                const gridStyle: React.CSSProperties = {
                  ...fullGridColsStyle,
                  gridTemplateRows: `repeat(${lanesCount}, 44px)`
                }

                return (
                  <div key={g.id} className={`grid gap-x-1 items-center ${idx > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''} py-2`} style={gridStyle}>
                    {/* Coluna do nome do grupo (ocupa todas as linhas) */}
                    <div className="row-span-full flex items-center text-xs font-semibold text-gray-700 dark:text-gray-200 pr-3">
                      {g.name}
                    </div>

                    {/* Colunas de meses (background leve) */}
                    {months.map((_, mIdx) => (
                      <div key={`bg-${mIdx}`} className="col-span-1 row-span-full h-[1px] bg-transparent" />
                    ))}

                    {/* Barras do grupo */}
                    {posItems.map((pi) => {
                      const item = itemsMap[pi.id]
                      const itemLabels = (item?.label_ids || [])
                        .map((id: string) => labelsMap[id])
                        .filter((l): l is typeof labelsMap[string] => !!l)
                      return (
                        <div
                          key={pi.id}
                          className="h-8 rounded bg-blue-500/80 dark:bg-blue-500/60 text-white px-2 flex items-center gap-1.5 shadow-sm ring-1 ring-white/70"
                          style={{
                            gridColumnStart: pi.colStart + 1, // desloca por coluna de grupo
                            gridColumnEnd: pi.colEnd + 1,
                            gridRowStart: (laneOf[pi.id] || 0) + 1
                          }}
                          title={pi.title}
                        >
                          <span className="truncate text-[12px] flex-1">{pi.title}</span>
                          {itemLabels.length > 0 && (
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {itemLabels.slice(0, 3).map((l) => {
                                const labelColor = (l.color && l.color.trim()) ? l.color.trim() : '#ffffff'
                                return (
                                  <div
                                    key={l.id}
                                    className="w-3 h-3 rounded border-2 flex-shrink-0"
                                    style={{ 
                                      backgroundColor: labelColor,
                                      borderColor: 'rgba(255, 255, 255, 0.5)',
                                      minWidth: '12px', 
                                      minHeight: '12px' 
                                    }}
                                    title={l.name}
                                  />
                                )
                              })}
                              {itemLabels.length > 3 && (
                                <span className="text-[10px] opacity-75">+{itemLabels.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}

                    {groupItems.length === 0 && (
                      <div className="col-span-full text-xs text-gray-500 dark:text-gray-400">Sem itens neste grupo.</div>
                    )}
                  </div>
                )
            })}
          </div>
        </div>
  )

  return (
    <div className="relative">
      {/* Botão de expandir somente a timeline */}
      <div className="absolute top-2 right-2 z-10 flex gap-2">
        <button
          onClick={() => setExpanded(true)}
          className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Expandir timeline"
        >
          Expandir
        </button>
      </div>
      {/* Cabeçalho + linhas dentro do mesmo container de scroll horizontal */}
      <div className="p-3">
        {renderBody()}
      </div>

      {/* Overlay de expansão */}
      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/60">
          <div className="absolute inset-4 bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden flex flex-col">
            <div className="p-2 flex justify-end">
              <button
                onClick={() => setExpanded(false)}
                className="px-3 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                title="Fechar"
              >
                Fechar
              </button>
            </div>
            <div className="flex-1 p-3 overflow-hidden">
              {renderBody()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RoadmapTimeline


